import { z } from 'zod';
import { attachmentIdsSchema, attachmentSchema } from '../constants/attachments.js';
import { TASK_STATUSES } from '../constants/task-states.js';
import { uuidSchema } from './common.js';
const dueDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use a YYYY-MM-DD date');
export const taskListQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(500).default(50),
    q: z.string().trim().max(200).optional(),
    status: z.enum(['open', 'pending_review', 'done', 'all']).default('open'),
    /** `me` is the signed-in staff member, `everyone` the unassigned pool,
     *  a uuid one specific person. Absent means all tasks. */
    assignee: z.union([z.enum(['me', 'everyone']), uuidSchema]).optional(),
    /** `me` narrows to tasks the signed-in staff member created, whoever holds them now. */
    createdBy: z.literal('me').optional(),
    supportTicketId: uuidSchema.optional(),
    customerId: uuidSchema.optional(),
    vendorId: uuidSchema.optional(),
    rentalId: uuidSchema.optional(),
    /** `hide` leaves out tasks whose start date is still ahead (t1348); `only`
     *  lists just those; `include` lists everything. */
    scheduled: z.enum(['hide', 'only', 'include']).default('hide'),
});
export const taskListItemSchema = z.object({
    id: uuidSchema,
    taskNumber: z.string(),
    title: z.string(),
    status: z.enum(TASK_STATUSES),
    dueDate: dueDateSchema.nullable(),
    /** The task stays out of the lists until this day (t1348). */
    startDate: dueDateSchema.nullable(),
    assignedStaffId: uuidSchema.nullable(),
    assignedStaffName: z.string().nullable(),
    supportTicketId: uuidSchema.nullable(),
    supportTicketNumber: z.string().nullable(),
    customerId: uuidSchema.nullable(),
    customerName: z.string().nullable(),
    /** Every customer on the task (t1199), the primary one first. */
    customers: z.array(z.object({ id: uuidSchema, name: z.string() })),
    vendorId: uuidSchema.nullable(),
    vendorName: z.string().nullable(),
    /** The rental this task is about (t1170), when the system raised it for one. */
    rentalId: uuidSchema.nullable(),
    rentalNumber: z.string().nullable(),
    /** The task this one is a follow-up on, when it is part of a thread. */
    parentTaskId: uuidSchema.nullable(),
    parentTaskNumber: z.string().nullable(),
    /** Direct follow-ups that are still open — drives the warning on completing a parent. */
    openChildCount: z.number().int(),
    completedAt: z.iso.datetime().nullable(),
    completedByName: z.string().nullable(),
    /** What was done, as written when the task was marked done; null while open. */
    completionNote: z.string().nullable(),
    createdAt: z.iso.datetime(),
});
export const taskDetailSchema = taskListItemSchema.extend({
    details: z.string(),
    /** Files attached to the task (t1249). */
    attachments: z.array(attachmentSchema).default([]),
    createdByName: z.string().nullable(),
    reminderSentAt: z.iso.datetime().nullable(),
    updatedAt: z.iso.datetime(),
});
export const createTaskSchema = z.object({
    title: z.string().trim().min(1, 'Give the task a title').max(200),
    details: z.string().trim().max(5000).default(''),
    /** null or absent = everyone. */
    assignedStaffId: uuidSchema.nullable().default(null),
    dueDate: dueDateSchema.nullable().default(null),
    startDate: dueDateSchema.nullable().optional(),
    /** Set when the task is a follow-up on a support ticket. */
    supportTicketId: uuidSchema.nullable().default(null),
    /** Optional links to the customer and/or vendor the task is about. */
    customerId: uuidSchema.nullable().default(null),
    /** Every customer the task is about, primary first; when given it replaces `customerId`. */
    customerIds: z.array(uuidSchema).max(20).optional(),
    vendorId: uuidSchema.nullable().default(null),
    /** Makes this a follow-up on another task; ticket, customer and vendor are
     *  inherited from it unless given. Fixed at creation. */
    parentTaskId: uuidSchema.nullable().default(null),
    attachmentIds: attachmentIdsSchema.default([]),
});
/** Marking a task done always says what was done. */
export const completeTaskSchema = z.object({
    note: z.string().trim().min(3, 'Say what was done').max(5000),
});
/** Sending a task back to open always says what still needs doing (t1216). */
export const rejectTaskSchema = z.object({
    note: z.string().trim().min(3, 'Say what still needs doing').max(5000),
});
export const updateTaskSchema = z
    .object({
    title: z.string().trim().min(1).max(200),
    details: z.string().trim().max(5000),
    assignedStaffId: uuidSchema.nullable(),
    dueDate: dueDateSchema.nullable(),
    startDate: dueDateSchema.nullable(),
    customerId: uuidSchema.nullable(),
    /** Replaces the whole customer list, primary first; takes precedence over `customerId`. */
    customerIds: z.array(uuidSchema).max(20),
    vendorId: uuidSchema.nullable(),
    /** The full list of attached files; omitted leaves them alone. */
    attachmentIds: attachmentIdsSchema,
})
    .partial();
export const taskEventSchema = z.object({
    id: uuidSchema,
    fromStatus: z.enum(TASK_STATUSES).nullable(),
    toStatus: z.enum(TASK_STATUSES),
    actorType: z.enum(['staff', 'customer', 'kiosk', 'system']),
    actorLabel: z.string().nullable(),
    note: z.string().nullable(),
    createdAt: z.iso.datetime(),
});
/** An internal reply on a task (t1221). */
export const taskReplySchema = z.object({
    id: uuidSchema,
    authorId: uuidSchema.nullable(),
    authorName: z.string().nullable(),
    body: z.string(),
    attachments: z.array(attachmentSchema).default([]),
    createdAt: z.iso.datetime(),
});
export const createTaskReplySchema = z
    .object({
    body: z.string().trim().max(5000).default(''),
    attachmentIds: attachmentIdsSchema.default([]),
})
    .refine((v) => v.body.length > 0 || v.attachmentIds.length > 0, {
    message: 'Write a reply or attach a file',
    path: ['body'],
});
//# sourceMappingURL=task.js.map